/**
 * ProgramCosting — the per-program cost estimate view (DEV — costing).
 *
 * Inline-embeddable (like ProgramSolutions): the ModulePage mounts it as the
 * costing review surface, and it also backs the /programs/:programId/costing
 * deep link. Header shows the interview scalars (quantity / service life /
 * inflation) plus the first-class acquisition metrics (system cost / APUC /
 * PAUC). The body is the editable CostingGrid, split into color-of-money tabs
 * (RDT&E / Procurement / O&S) that the user tabs between; a button streams the
 * Excel export.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Download, Loader2 } from 'lucide-react'

import CostingGrid from '../../components/costing/CostingGrid'
import {
  APPROPRIATION_LABELS,
  APPROPRIATION_ORDER,
  costingApi,
  type Appropriation,
  type CostModelGrid,
} from '../../services/costing'

interface ProgramCostingProps {
  /** Render without page chrome (padding / max-width) for inline embedding. */
  embedded?: boolean
  /** Program id override; falls back to the route param when omitted. */
  programId?: string
  /** Optional explicit cost model id (from the review surface context). */
  costModelId?: number
}

type TabKey = 'all' | Appropriation

/** Compact money for headline figures: $265.4M, $84.7M, $1.2B. */
function fmtCompact(value: string | null | undefined): string {
  if (value == null) return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`
  return `$${n.toFixed(0)}`
}

/** Full dollars for per-unit figures: $423,548. */
function fmtDollars(value: string | null | undefined): string {
  if (value == null) return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2" title={hint}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  )
}

const ProgramCosting = ({ embedded = false, programId: programIdProp }: ProgramCostingProps = {}) => {
  const params = useParams<{ programId: string }>()
  const programId = programIdProp ?? params.programId
  const [grid, setGrid] = useState<CostModelGrid | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [tab, setTab] = useState<TabKey>('all')

  const load = useCallback(async () => {
    if (!programId) return
    setError(null)
    try {
      const g = await costingApi.getByProgram(programId)
      setGrid(g)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [programId])

  useEffect(() => { load() }, [load])

  // Colors of money present among the model's top-level categories, in canonical
  // order. Tabs only appear when the estimate spans more than one color.
  const colors = useMemo<Appropriation[]>(() => {
    const present = new Set((grid?.categories ?? []).map((c) => c.appropriation || ''))
    return APPROPRIATION_ORDER.filter((a) => present.has(a))
  }, [grid])

  // The grid filtered to the active tab, with per-year totals recomputed from the
  // visible categories' subtotals so the total row matches what's shown.
  const view = useMemo<CostModelGrid | null>(() => {
    if (!grid || tab === 'all') return grid
    const cats = grid.categories.filter((c) => (c.appropriation || '') === tab)
    const grand: Record<string, string> = {}
    let overall = 0
    for (const y of grid.years) {
      const sum = cats.reduce((a, c) => a + Number(c.year_subtotals[String(y)] ?? 0), 0)
      grand[String(y)] = sum.toFixed(2)
      overall += sum
    }
    grand.overall = overall.toFixed(2)
    return { ...grid, categories: cats, grand_totals: grand }
  }, [grid, tab])

  const onDownload = async () => {
    if (!grid) return
    setDownloading(true)
    try {
      await costingApi.exportXlsx(grid.id)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground p-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading cost estimate…
      </div>
    )
  }
  if (error) {
    return <div className="text-sm text-destructive p-4">Failed to load cost estimate: {error}</div>
  }
  if (!grid || !view) {
    return (
      <div className="text-sm text-muted-foreground p-4">
        No cost estimate yet. Complete the costing interview and run generation.
      </div>
    )
  }

  const inflationPct = (Number(grid.inflation_rate) * 100).toFixed(1)
  const metrics = grid.unit_cost_metrics
  const showTabs = colors.length > 1
  const subtotals = metrics?.color_subtotals ?? {}

  return (
    <div className={embedded ? '' : 'p-6 mx-auto max-w-6xl'}>
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Cost Estimate</h2>
          <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mt-1">
            <span>Quantity: <strong>{grid.program_quantity ?? '—'}</strong></span>
            <span>Service life: <strong>{grid.service_life_years ?? 0} yr</strong></span>
            <span>Inflation: <strong>{inflationPct}%</strong></span>
            <span>Years: <strong>{grid.base_year}–{grid.base_year + grid.year_span - 1}</strong></span>
          </div>
        </div>
        <button
          onClick={onDownload}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-60"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download to Excel
        </button>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
          <Metric label="System cost" value={fmtCompact(metrics.system_cost)}
                  hint="Total life-cycle cost across all colors of money" />
          <Metric label="APUC" value={fmtDollars(metrics.apuc)}
                  hint="Average Procurement Unit Cost = procurement ÷ quantity" />
          <Metric label="PAUC" value={fmtDollars(metrics.pauc)}
                  hint="Program Acquisition Unit Cost = (RDT&E + procurement) ÷ quantity" />
        </div>
      )}

      {showTabs && (
        <div className="flex flex-wrap gap-1 border-b border-border mb-4">
          {(['all', ...colors] as TabKey[]).map((key) => {
            const active = tab === key
            const label = key === 'all' ? 'All' : (APPROPRIATION_LABELS[key] ?? key)
            const amount = key === 'all' ? metrics?.system_cost : subtotals[key]
            return (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${
                  active
                    ? 'border-primary text-foreground font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
                {amount != null && (
                  <span className="ml-1.5 text-xs text-muted-foreground tabular-nums">
                    {fmtCompact(amount)}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      <CostingGrid grid={view} onChange={load} moveTargets={grid.categories} />
    </div>
  )
}

export default ProgramCosting
